const mediasoup = require("mediasoup-client");

export default class MediasoupSubscriber {
  constructor(config) {
    this.url = (config.url || "").replace(/\/$/, "") + "/ws";
    this.token = config.token;
    this.video = config.video;
    this.onState = config.onState || function() {};
    this.socket = null;
    this.device = null;
    this.transport = null;
    this.stream = new MediaStream();
    this.connectCallback = null;
    this.keepAliveTimer = null;
    this.reconnectTimer = null;
    this.producerId = "";
    this.stopped = false;
  }

  connect() {
    this.stopped = false;
    this.onState("connecting");
    this.openSocket();
  }

  openSocket() {
    if (this.stopped) {
      return;
    }

    this.socket = new WebSocket(this.url);
    this.socket.addEventListener("open", () => {
      this.send("getRouterRtpCapabilities");
      this.startKeepAlive();
    });
    this.socket.addEventListener("message", (event) => {
      this.handleMessage(event.data);
    });
    this.socket.addEventListener("close", () => {
      this.stopKeepAlive();
      if (!this.stopped) {
        this.onState("reconnecting");
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => this.openSocket(), 1200);
      }
    });
    this.socket.addEventListener("error", () => {
      this.onState("error");
      if (this.socket) {
        this.socket.close();
      }
    });
  }

  async handleMessage(raw) {
    let response;
    try {
      response = JSON.parse(raw);
    } catch (error) {
      return;
    }

    switch (response.type) {
      case "routerCap":
        await this.loadDevice(response.data);
        this.subscribe();
        break;
      case "subTransportCreated":
        this.createTransport(response);
        break;
      case "subConnected":
        if (this.connectCallback) {
          const callback = this.connectCallback;
          this.connectCallback = null;
          callback();
        }
        break;
      case "subscribed":
        await this.consumeTracks(response.data || {});
        break;
      case "closeSubscribed":
        this.clearVideo();
        this.onState("waiting");
        break;
      case "keepAlive":
        this.handleKeepAlive(response.data || {});
        break;
      default:
        break;
    }
  }

  async loadDevice(routerRtpCapabilities) {
    this.device = new mediasoup.Device();
    await this.device.load({ routerRtpCapabilities });
  }

  subscribe() {
    this.closeTransport();
    this.stream = new MediaStream();
    this.send("createConsumerTransport", { forceTcp: false });
  }

  createTransport(response) {
    if (!this.device || response.error) {
      this.onState("error");
      return;
    }

    this.transport = this.device.createRecvTransport(response.data);
    this.transport.on("connect", ({ dtlsParameters }, callback) => {
      this.connectCallback = callback;
      this.send("connectConsumerTransport", {
        transportId: this.transport.id,
        dtlsParameters,
      });
    });
    this.transport.on("connectionstatechange", (state) => {
      if (state === "connected") {
        this.video.srcObject = this.stream;
        this.video.style.display = "block";
        this.video.play().catch(() => {});
        this.send("resume");
        this.onState("playing");
      } else if (state === "failed" || state === "closed") {
        this.onState("error");
      }
    });
    this.send("consume", { rtpCapabilities: this.device.rtpCapabilities });
  }

  async consumeTracks(data) {
    if (!this.transport || !data.video) {
      this.onState("waiting");
      return;
    }

    await this.consumeTrack(data.video);
    if (data.audio) {
      await this.consumeTrack(data.audio);
    }
    this.producerId = data.video.produtorPlay || data.video.producerId || "";
  }

  async consumeTrack(item) {
    const consumer = await this.transport.consume({
      id: item.id,
      producerId: item.producerId,
      kind: item.kind,
      rtpParameters: item.rtpParameters,
      codecOptions: {},
    });
    this.stream.addTrack(consumer.track);
  }

  handleKeepAlive(data) {
    if (!data.status) {
      return;
    }

    if (!this.producerId) {
      this.producerId = data.produtorPlay || "";
      return;
    }

    if (data.produtorPlay && data.produtorPlay !== this.producerId) {
      this.producerId = data.produtorPlay;
      this.subscribe();
    }
  }

  startKeepAlive() {
    this.stopKeepAlive();
    this.keepAliveTimer = setInterval(() => {
      this.send("keepAlive");
    }, 5000);
  }

  stopKeepAlive() {
    clearInterval(this.keepAliveTimer);
    this.keepAliveTimer = null;
  }

  send(type, data) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type, token: this.token, ...(data || {}) }));
  }

  clearVideo() {
    if (!this.video) {
      return;
    }
    this.video.pause();
    this.video.srcObject = null;
    this.video.style.display = "none";
  }

  closeTransport() {
    if (this.transport) {
      try {
        this.transport.close();
      } catch (error) {
        // O transporte pode já ter sido encerrado pelo servidor.
      }
      this.transport = null;
    }
  }

  disconnect() {
    this.stopped = true;
    clearTimeout(this.reconnectTimer);
    this.stopKeepAlive();
    this.closeTransport();
    if (this.socket) {
      try {
        this.socket.close();
      } catch (error) {
        // O socket pode já estar fechado.
      }
      this.socket = null;
    }
    this.clearVideo();
  }
}
